import React, { useState } from 'react';
import { Plus, Trash2, CreditCard, Receipt, Wallet } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';

const PaymentModeList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: modesResponse, refresh, loading } = useGet(`/payment-modes?page=${currentPage}`);
    
    const modes = modesResponse?.data || [];
    const pagination = modesResponse?.meta || null;

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/payment-modes', { name, code });
            setName(''); 
            setCode(''); 
            refresh();
        } catch (err) { 
            alert('خطأ أثناء إضافة وسيلة الدفع'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف وسيلة الدفع هذه؟')) {
            try {
                await api.delete(`/payment-modes/${id}`);
                refresh();
            } catch (err) {
                alert('خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl  text-slate-800">وسائل الدفع</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1 ">إدارة طرق التحصيل المالي المتاحة (نقداً، شيك، تحويل...)</p>
                </div>
                <div className="flex bg-white px-4 py-2 rounded-lg border border-slate-100 items-center gap-2">
                    <Wallet className="text-primary w-5 h-5" />
                    <span className="text-sm  text-slate-600">الوسائل المتوفرة: {pagination?.total || 0}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Form Section */}
                <Card className="p-4 h-fit lg:col-span-1 border-primary/10 bg-orange-50/10">
                    <h3 className="text-lg  text-slate-800 mb-5 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-primary" /> تعريف وسيلة دفع
                    </h3>
                    <form onSubmit={handleAdd} className="space-y-4">
                        <Input 
                            label="كود الوسيلة" 
                            value={code} 
                            onChange={e => setCode(e.target.value)} 
                            placeholder="مثال: CASH, CHECK, BANK" 
                            required 
                        />
                        <Input 
                            label="اسم الوسيلة" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            placeholder="مثال: نقداً" 
                            required 
                        />
                        <Button type="submit" isLoading={isSubmitting} className="w-full mt-2">
                            تعيين الوسيلة
                        </Button>
                    </form>
                </Card>

                {/* List Section */}
                <Card className="lg:col-span-2 overflow-hidden border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest">الكود</th>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest">اسم وسيلة الدفع</th>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-10 text-center text-slate-400 animate-pulse">جاري جلب البيانات...</td>
                                    </tr>
                                )}
                                {!loading && modes?.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-20 text-center text-slate-400">
                                            <div className="max-w-xs mx-auto">
                                                <Receipt className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                                                <p className=" text-slate-600 mb-2">لا توجد وسائل دفع معرفة حالياً</p>
                                                <p className="text-xs text-slate-400">يرجى إضافة وسيلة دفع واحدة على الأقل (مثل: نقداً، شيك، تحويل) لتظهر لك في القائمة عند تسجيل العمليات.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {modes?.map(mode => (
                                    <tr key={mode.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4 px-6 py-4">
                                            <span className=" text-slate-600 bg-slate-100 px-3 py-1 rounded text-[11px] uppercase tracking-tighter">
                                                {mode.code}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                                                    <CreditCard size={14} />
                                                </div>
                                                <span className=" text-slate-800">{mode.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-left">
                                            <button 
                                                onClick={() => handleDelete(mode.id)} 
                                                className="p-2 text-slate-300 hover:text-red-500 transition-colors bg-red-50/0 hover:bg-red-50 rounded-lg"
                                                title="حذف الوسيلة"
                                            >
                                                <Trash2 size={18}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination && pagination.last_page > 1 && (
                        <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                            <div className="text-xs text-slate-500 font-normal">
                                عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> وسيلة
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="h-8 px-3 text-xs"
                                >
                                    السابق
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (pagination.last_page > 5 && currentPage > 3) {
                                            pageNum = currentPage - 3 + i + 1;
                                            if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                                    currentPage === pageNum 
                                                    ? 'bg-primary text-white shadow-sm font-medium' 
                                                    : 'text-slate-500 hover:bg-slate-100'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page}
                                    className="h-8 px-3 text-xs"
                                >
                                    التالي
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default PaymentModeList;
